import { EventStatus } from "../shared/enums";
import type { AccommodationsRow, RoomsByNight } from "@/lib/utils/accommodations";
import type { RfpDraftDisclosure } from "@/lib/rfp/disclosure";

/** A start–end time range, `HH:mm`; `""` when not given. */
export interface MeetingSpaceTimeRange {
  startTime: string;
  endTime: string;
}

/** One day a meeting space is needed and its time ranges on that day. `date` is `YYYY-MM-DD`, or
 *  `null` when the event has no fixed dates yet. */
export interface MeetingSpaceDay {
  date: string | null;
  times: MeetingSpaceTimeRange[];
}

/** Optional "when" for a meeting space: the days it's needed, sorted, each with its own times.
 *  Empty `days` means the planner didn't say. */
export interface MeetingSpaceSchedule {
  days: MeetingSpaceDay[];
}

const asString = (v: unknown): string => (typeof v === "string" ? v : "");
const asRange = (v: Record<string, unknown>): MeetingSpaceTimeRange => ({
  startTime: asString(v.startTime),
  endTime: asString(v.endTime),
});
const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object";

/**
 * Reads a stored meeting-space schedule. Also accepts the earlier shapes written during
 * development: `{ dates, slots }` and `{ date | dates, startTime, endTime }`.
 *
 * @param raw - The jsonb value, possibly missing or partial.
 * @returns A well-formed schedule; empty when nothing usable is stored.
 */
export function normalizeMeetingSpaceSchedule(raw: unknown): MeetingSpaceSchedule {
  const value = isRecord(raw) ? raw : {};
  if (Array.isArray(value.days)) {
    return {
      days: value.days.filter(isRecord).map((day) => ({
        date: typeof day.date === "string" ? day.date : null,
        times: Array.isArray(day.times) ? day.times.filter(isRecord).map(asRange) : [],
      })),
    };
  }
  const dates = Array.isArray(value.dates)
    ? value.dates.filter((d): d is string => typeof d === "string")
    : typeof value.date === "string"
      ? [value.date]
      : [];
  const slots = Array.isArray(value.slots)
    ? value.slots
        .filter(isRecord)
        .map((slot) => ({ date: typeof slot.date === "string" ? slot.date : null, ...asRange(slot) }))
    : asString(value.startTime) || asString(value.endTime)
      ? [{ date: null, ...asRange(value) }]
      : [];
  const timesOn = (date: string | null) =>
    slots
      .filter((slot) => slot.date === date || slot.date === null)
      .map(({ startTime, endTime }) => ({ startTime, endTime }));
  if (!dates.length) return { days: slots.length ? [{ date: null, times: timesOn(null) }] : [] };
  return { days: dates.map((date) => ({ date, times: timesOn(date) })) };
}

/** Shape of `events.meeting_room_requirements` / `draft_events.meeting_room_requirements` — a
 *  direct mirror of the create-event-v3 wizard's `GeneralSessionData`/`BreakoutData` TS types.
 *  Added in migrations/20260822_add_meeting_room_requirements_to_events_and_draft_events.sql. */
export interface MeetingRoomRequirements {
  generalSession?: {
    needed: boolean | null;
    roomCount: number;
    capacity: number | null;
    roomTypes: string[];
    notes: string;
    schedule?: MeetingSpaceSchedule;
  };
  breakout?: {
    needed: boolean | null;
    count: number;
    capacityMin: number | null;
    capacityMax: number | null;
    roomTypes: string[];
    notes: string;
    schedule?: MeetingSpaceSchedule;
  };
}

// Table for managing draft events

export interface DraftEvents {
  uuid: string; // Unique identifier for each draft event
  draft_event_id: number; // Unique identifier for each draft event
  event_id?: number; // Optional event ID for the draft event
  org_id?: number; // Optional organization ID for the draft event
  name?: string; // Name of the draft event
  venue_type?: string; // Type of the venue
  location?: string; // Location of the event
  formatted_locations?: Array<{
    city: string;
    state: string | null;
    country: string | null;
    formatted_address: string;
    place_id: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  }>; // Formatted location data
  num_people?: number; // Number of people attending
  other_notes?: string; // Additional notes for the event
  flexibility?: string; // Flexibility details
  origin?: string; // Origin information
  meeting_rooms?: string; // Meeting room requirements
  food_beverage_requirements?: string; // Food and beverage requirements
  decision_deadline?: Date; // Decision deadline with time zone
  venue_data?: any; // JSON data for venue information
  created_at: Date; // Timestamp when the draft was created
  venues_browsed?: string; // Venues browsed by the customer

  user_first_name?: string; // First name of the user
  user_last_name?: string; // Last name of the user
  user_email?: string; // Email of the user
  user_title?: string; // Title of the user
  user_company?: string; // Company of the user
  user_phone_number?: string; // Phone number of the user (digits only, without dial code)
  user_country_dial_code_id?: number | null; // Reference to countries_dial_codes table
  user_industry?: string; // Industry of the user
  user_headquarters_city?: string; // Headquarter city of the user
  is_interested_in_other_rfps?: boolean; // Whether the user is interested in requesting RFPs from other venues as well
  is_multi_day?: boolean; // Whether the event is multi-day
  is_northstar_event?: boolean; // Whether the event is a Northstar event

  // New fields added to schema
  budget_breakdown?: {
    flight: number;
    venue: number;
    fb: number;
    transport: number;
    merch: number;
    activities: number;
    av: number;
    other: number;
  };
  notes_location_budget?: string; // Freeform notes about budget constraints and location requirements
  event_dates?: Array<{
    date?: string; // For single-day events (YYYY-MM-DD)
    startTime?: string; // For single-day events (HH:MM)
    endTime?: string; // For single-day events (HH:MM)
    startDate?: string; // For multi-day events (YYYY-MM-DD)
    endDate?: string; // For multi-day events (YYYY-MM-DD)
  }>;
  // Two shapes exist in prod: the current array (`AccommodationsRow`) and the
  // legacy date-keyed object (`RoomsByNight`). Mirrors `Events.accommodations`
  // below — readers must handle BOTH via `normalizeAccommodationsToArray()`,
  // never assume a shape.
  accommodations?: AccommodationsRow | RoomsByNight;
  agenda?: Array<{
    id: string;
    title: string;
    dates: string[];
    startTime: string;
    endTime: string;
    roomType: string;
    capacity: number;
    avRequirements: string[];
    fbRequirements: string[];
    avOtherText: string;
    fbOtherText: string;
    notes: string;
  }>;
  event_source?: string; // Google Search, Referral/Word of Mouth, Previous Client, etc.
  event_source_other_text?: string; // Required when event_source is "Other"
  decision_factors?: string[]; // Price/Budget, Location/Accessibility, etc.
  decision_factor_other_text?: string; // Required when decision_factor includes "Other"
  additional_services?: string[]; // Flight Bookings, Activities and Excursions, etc.
  custom_questions?: string[]; // JSONB field for custom questions for venues
  custom_concessions?: string[]; // JSONB field for custom concessions for venues
  skip_sending_rfp?: boolean; // Whether to skip sending RFPs for custom and northstar venues (admin only)
  // Draft-only wizard wrapper for the create-event-v3 Meeting-rooms step — NOT the same shape as
  // `Events.meeting_room_dates` below (that one is real date ranges owned by the enterprise agenda).
  // See `DraftMeetingRoomsValue` in `create-event-v3/_logic/draft-actions.ts`.
  meeting_room_dates?: {
    // `null` = the wizard's meeting-rooms gate hadn't been answered yet when this draft was saved
    // (e.g. autosave fired from an earlier step) — distinct from `false` (explicitly answered "no").
    needed: boolean | null;
    start: string | null;
    end: string | null;
    numRooms: number;
    detailed: { [date: string]: number } | null;
    // Food & beverage step's service pills (service -> event days). Draft-only; the event row gets
    // them folded into `food_beverage_requirements` text.
    foodBeverageMeals?: Record<string, string[]>;
  };
  num_meeting_rooms_needed?: number | null; // smallint; meeting rooms needed. null = never asked.
  meeting_room_requirements?: MeetingRoomRequirements;
}
// Table for managing events created by users

export interface Events {
  event_id: number; // Unique identifier for each event
  org_id: number; // The organization that owns the event
  event_status: EventStatus; // The current status of the event (e.g., Waiting on deposit, in-progress, completed)
  created_at: string; // milli since epoch; The date and time the event was created (e.g., 2024-01-02 14:30:00)

  name: string; // Name of the event (e.g., Annual Conference 2024)
  user_email: string; // Email of the user who created the event

  venue_type: string; // Type of venue (e.g., boutique hotel, conference center, hotel, restaurant)
  location: string; // The geographical location of the event (e.g., New York City)
  formatted_locations?: Array<{
    city: string;
    state: string | null;
    country: string | null;
    formatted_address: string;
    place_id: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  }>; // Formatted location data
  num_people: number; // integer; the number of people expected to attend (e.g., 100)
  num_hotel_rooms_needed?: number; // peak total hotel rooms (wizard scalar; JSONB `accommodations` is the richer source)
  num_meeting_rooms_needed?: number | null; // smallint; meeting rooms needed (wizard scalar). null = never asked. Added in #1530.

  decision_deadline?: Date; // Example: "Decision by end of month" - Details about decision deadline
  other_notes?: string; // (e.g., "Wanted something more boutique") - Initial feedback from the customer
  email_attachment?: string; // Example: "event_details.pdf" - Details about email attachments
  flexibility?: string; // Example: "Flexible within a week" - Details about date flexibility
  origin?: string; // Optional: the origin location attendees are coming from (e.g., Los Angeles)
  meeting_rooms?: string; // Optional: details about meeting room requirements (e.g., Main Hall, Breakout Room A)
  food_beverage_requirements?: string; // Optional: details about food and beverage needs (e.g., Vegetarian options, Open bar)

  email_label?: string;
  event_status_last_update: string;

  rfp_email_body?: string; // email body for the RFP email
  rfp_email_subject?: string; // email subject for the RFP email

  reply_email_body?: string; // the last reply sent through the admin dashboard

  chain_rep_email_body?: string; // the last chain rep email sent through the admin dashboard

  // ENG-150 RFP disclosure controls. All three are excluded from event duplication
  // (see event-duplication/_logic/constants.ts) and must only be written via
  // admin-gated server actions (requireNowadaysAdmin) — events RLS is row-level,
  // so any customer with edit access could otherwise write them.
  include_org_name_in_rfp_override?: boolean | null; // per-event override of orgs.org_preferences.include_org_name_in_rfp; null/unset = inherit org preference
  venue_facing_event_name?: string | null; // event name shown to venues in RFP emails; always wins when set. null = raw `name` when disclosed, omitted when anonymized
  required_rfp_questions?: unknown; // this event's own required questions (same shape as the org's); null = use the org's list
  rfp_draft_disclosure?: RfpDraftDisclosure | null; // per-draft staleness snapshots keyed "rfp"/"rep"; null = legacy draft (see src/lib/rfp)

  nowadays_percentage_discount?: number; // decimal; The percentage discount given by Nowadays

  expected_commission?: number; // Expected commission amount for the event
  received_commission?: number; // Commission amount actually received for the event
  date_expected_commission?: string; // The expected date by which the commission is to be received
  date_received_commission?: string; // The actual date on which the commission was received

  default_payment_method_id?: number; // the default payment method for the event

  internal_status?: string; // internal nowadays status of the event
  month_closed: string; // date we closed the customer event (usually when they paid the deposit)
  books_closed: boolean; // whether the books are closed for the event
  payment_notes?: string; // notes about the payments and finances and the current status
  currency: string; // currency of the event

  internal_event_notes?: string; // internal notes about the event

  is_northstar_event?: boolean;

  deposit_amount_paid?: number; // amount paid for the deposit

  context?: string; // additional context about the event

  quo_number?: string | null; // E.164 outbound SMS sender for Quo/Twilio text blasts. null = use event default / legacy context / env.

  time_zone?: string | null; // IANA time zone (e.g. "America/Los_Angeles"); null means floating time. Drives hero countdown, schedule, activity slots, and .ics generation.

  checkin_enabled?: boolean; // whether check-in is enabled for the event — gates the rsvp Check-in tab and the attendee dashboard QR pass card. Defaults to false (opt-in per event).

  email_header_image_path?: string | null; // admin-chosen header image shown across this event's email templates (activity + calendar invite). Falls back to the event's enterprise org logo, then the default Nowadays logo, when unset. See resolveEventHeaderImageUrl in features/landing-page-v2/_logic/email/enterpriseTheme.ts.

  badge_template_config?: Record<string, unknown> | null; // name-badge template + first/last name overlay placement for the check-in auto-print flow. See BadgeTemplateConfig in features/checkin/_logic/constants.ts (read via normalizeBadgeTemplateConfig, which also migrates the pre-split single-name-position shape). Null when unconfigured.

  room_type_links?: Record<string, string> | null; // room_type value -> custom "more info" link (e.g. listing/photos), admin-editable. Null when unconfigured.

  total_num_nights_quote_override?: number; // override for the total number of nights in the quote

  venue_data?: any; // JSON data for venue information

  cc_email_in_rfps?: string; // email addresses to cc in the RFPs

  booking_outcome_notes?: string; // Notes explaining why event was booked off platform or why it didn't move forward

  // New fields added to schema
  budget_breakdown?: {
    flight: number;
    venue: number;
    fb: number;
    transport: number;
    activities: number;
    other: number;
  };
  notes_location_budget?: string; // Freeform notes about budget constraints and location requirements
  event_dates?: Array<{
    date?: string; // For single-day events (YYYY-MM-DD)
    startTime?: string; // For single-day events (HH:MM)
    endTime?: string; // For single-day events (HH:MM)
    startDate?: string; // For multi-day events (YYYY-MM-DD)
    endDate?: string; // For multi-day events (YYYY-MM-DD)
  }>;
  // Inclusive {startDate,endDate} ranges owned by the enterprise agenda's date
  // pickers. `event_dates` is DERIVED from this ∪ the guestroom ranges (see
  // event-details-rooms/_logic/rooms.ts `deriveEventDates`). Added with #1626-followup.
  meeting_room_dates?: Array<{ startDate: string; endDate: string }>;
  // Per-day room-count map (date -> count) for the Meeting-rooms detail-grid toggle.
  // Distinct from `meeting_room_dates` above (a range) and `num_meeting_rooms_needed`
  // (a derived peak scalar) — added in migrations/20260817_add_meeting_room_counts_by_date_to_events.sql.
  meeting_room_counts_by_date?: Record<string, number>;
  // General-session + breakout requirement breakdown from the Meeting-rooms step — added in
  // migrations/20260822_add_meeting_room_requirements_to_events_and_draft_events.sql.
  meeting_room_requirements?: MeetingRoomRequirements;
  is_multi_day?: boolean;
  // Two shapes exist in prod: the legacy date-keyed object (`RoomsByNight`) and the
  // current array (`AccommodationsRow` from @/lib/utils/accommodations, written by the
  // wizard and the enterprise Rooms editor since #1530). Readers must handle BOTH — use
  // `normalizeAccommodationsToArray()` / `getPeakRoomCount()` rather than assuming a shape.
  accommodations?: AccommodationsRow | RoomsByNight;
  agenda?: Array<{
    id: string;
    title: string;
    dates: string[];
    startTime: string;
    endTime: string;
    roomType: string;
    capacity: number;
    avRequirements: string[];
    fbRequirements: string[];
    avOtherText: string;
    fbOtherText: string;
    notes: string;
  }>;
  event_source?: string; // Google Search, Referral/Word of Mouth, Previous Client, etc.
  event_source_other_text?: string; // Required when event_source is "Other"
  decision_factors?: string[]; // Price/Budget, Location/Accessibility, etc.
  decision_factor_other_text?: string; // Required when decision_factor includes "Other"
  additional_services?: string[]; // Flight Bookings, Activities and Excursions, etc.
  custom_questions?: string[]; // JSONB field for custom questions for venues
  custom_concessions?: string[]; // JSONB field for custom concessions for venues
}

export interface EventChange {
  event_change_id: number; // primary key
  event_id: number; // Reference to the event
  field_name: keyof Events; // Name of the field that was changed
  old_value: string | null; // Previous value
  new_value: string | null; // New value
  created_at: string; // Timestamp of when the change was made
  created_by_user_id: number; // User who made the change
}
// used for admin dashboard

export interface DraftEventChange {
  draft_event_change_id: number; // primary key
  draft_event_id: number; // Reference to the draft event
  field_name: keyof DraftEvents; // Name of the field that was changed
  old_value: string | null; // Previous value
  new_value: string | null; // New value
  created_at: string; // Timestamp of when the change was made
  created_by_user_id?: number | null; // User who made the change (optional for draft events)
}

export interface EventsAdminData {
  created_at: string;
  event_id: number;
  hide_event: boolean;
  comments: string;
  claimed_by_user_id: number | null;
  admin_notes: string;
  admin_notes_last_updated: string;
  invited_to_slack: boolean;
  flag_actionable?: string;
  custom_highlighted_row_color?: string;
}

export interface EventsVenueStats {
  event_id: number;
  num_venues_liked: number;
  num_venues_with_quotes: number;
  liked_venue_ids: string[];
  quoted_venue_ids: string[];
  num_liked_venues_with_quotes: number;
  most_recent_quote: string | null;
  num_needs_rfp: number;
  num_venues_with_green_quotes: number;
  num_venues_with_quotes_showing: number;
}

export interface EventMessages {
  event_id: number;
  inbox_count: number;
  messages: any[];
  status: string;
  updated_at: string;
}
